import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { InferResponseType } from 'hono/client';
import { GripVertical, MoreVertical, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import type { api } from '~/lib/api';

type StagesResponse = InferResponseType<(typeof api.api.org)[':orgId']['workflow-stages']['$get']>;
export type StageItem = StagesResponse['stages'][number];

export function WorkflowStageRow({
  stage,
  onEdit,
  onDelete
}: {
  stage: StageItem;
  onEdit: (stage: StageItem) => void;
  onDelete: (stage: StageItem) => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.id,
    disabled: stage.isSystem
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 ${
        isDragging ? 'opacity-50 shadow-md' : ''
      }`}
    >
      {stage.isSystem ? (
        <div className="size-4" />
      ) : (
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
        >
          <GripVertical className="size-4" />
        </div>
      )}
      {stage.color && (
        <div className="size-3 shrink-0 rounded-full" style={{ backgroundColor: stage.color }} />
      )}
      <span className="flex-1 text-sm font-medium">{stage.name}</span>
      {stage.isSystem && (
        <Badge variant="secondary" className="gap-1 text-xs">
          <Shield className="size-3" />
          {t('workflows.stages.system')}
        </Badge>
      )}
      {!stage.isSystem && (
        <DropdownMenu>
          <DropdownMenuTrigger className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent">
            <MoreVertical className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                onEdit(stage);
              }}
            >
              {t('actions.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => {
                onDelete(stage);
              }}
            >
              {t('actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
